import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMesterplanApi } from '@mesterplan/web-hooks';
import type { ApiResult, MesterplanApi, TrackrsAttentionDto } from '@mesterplan/api-client';
import type { CreateProjectModel } from '@mesterplan/shared-types';
import { useProjectOperations } from '@mesterplan/shared-firebase';
import { useCompany } from '@/contexts/CompanyContext';
import { useNetUser } from '@/hooks/useNetUser';
import { usePermissions } from '@/hooks/usePermissions';
import {
  clearSameProjectRows,
  deferAttentionRow,
  removeAttentionRow,
  snoozeAttention,
  snoozeUntil,
  SNOOZE_DAYS,
  type AttentionRowKind,
  type DismissableAttentionKind,
} from '@/hooks/trackrsAttention.utils';

/**
 * One cache entry per active org, shared by the page, the Projekter nav badge,
 * the Trackrs card banner and the first-visit modal — so a row dismissed on the
 * page takes the badge count down with it, and a snooze clears all four.
 */
export const TRACKRS_ATTENTION_QUERY_KEY = 'trackrs-attention';

export function trackrsAttentionQueryKey(orgId: string | null): readonly [string, string | null] {
  return [TRACKRS_ATTENTION_QUERY_KEY, orgId] as const;
}

/** Thrown by the query and the mutations so callers can branch on the HTTP status for copy. */
export class TrackrsAttentionError extends Error {
  constructor(
    readonly status: number,
    detail?: string,
  ) {
    super(detail ?? `Trackrs attention request failed (${status})`);
    this.name = 'TrackrsAttentionError';
  }
}

function unwrap<T>(res: ApiResult<T>): T {
  if (!res.ok) throw new TrackrsAttentionError(res.status, res.error?.detail);
  return res.data;
}

export interface TrackrsAttentionState {
  /** The nudge model, or null until loaded / when unavailable. */
  data: TrackrsAttentionDto | null;
  /**
   * False when nothing about the nudge should render: the user lacks
   * `integrations.connections.manage`, or the backend answered 404 (the
   * controller does that while `RsIntegrations:Enabled` is off).
   */
  available: boolean;
  /** Shorthand for the badge: 0 while loading, unavailable or snoozed. */
  openCount: number;
  isLoading: boolean;
  error: TrackrsAttentionError | null;
  refetch: () => void;
}

/**
 * `GET /api/integrations/trackrs/attention` for the active org. Same gate as
 * `useIntegrationConnections` (permission + active org), on react-query so the
 * actions below can edit the cache optimistically and every reader re-renders.
 */
export function useTrackrsAttention(): TrackrsAttentionState {
  const api = useMesterplanApi() as unknown as MesterplanApi;
  const { user } = useNetUser();
  const { hasPermission } = usePermissions();

  const activeOrgId = user?.activeOrgId ?? null;
  const canManage = hasPermission('integrations.connections.manage');
  const enabled = activeOrgId !== null && canManage;

  const query = useQuery<TrackrsAttentionDto, TrackrsAttentionError>({
    queryKey: trackrsAttentionQueryKey(activeOrgId),
    enabled,
    queryFn: async () => unwrap(await api.integrations.getTrackrsAttention()),
    // The badge sits in the sidebar of every page; a minute of staleness is fine
    // there, and every action invalidates explicitly.
    staleTime: 60_000,
    retry: false,
    // "Trackrs svarer ikke lige nu — prøver igen": poll while the remote pull is failing.
    refetchInterval: (q) => (q.state.data?.remoteUnavailable ? 30_000 : false),
  });

  const backendDisabled = query.error?.status === 404;
  const data = query.data ?? null;

  return {
    data,
    available: canManage && !backendDisabled,
    openCount: data?.openCount ?? 0,
    isLoading: enabled && query.isPending,
    error: query.error ?? null,
    refetch: () => {
      void query.refetch();
    },
  };
}

type Snapshot = { previous: TrackrsAttentionDto | undefined };

/**
 * The per-row and whole-page actions. Each one removes (or defers) its row in the
 * cache before the request, puts the snapshot back if the request fails, and
 * refetches when it settles — so "Ignorér" feels instant, and a 409 from a
 * colleague deciding the same row first simply re-renders the truth.
 */
export function useTrackrsAttentionActions() {
  const api = useMesterplanApi() as unknown as MesterplanApi;
  const queryClient = useQueryClient();
  const { user } = useNetUser();
  const { selectedCompany } = useCompany();
  const activeOrgId = user?.activeOrgId ?? null;
  const key = trackrsAttentionQueryKey(activeOrgId);

  // Field completions go through the same GET→merge→PUT path EditProjectDialog
  // uses, so a customer/case-number/start-date fix never drops another field.
  const { useUpdateProject } = useProjectOperations({
    selectedCompany: selectedCompany ? { id: selectedCompany.id } : null,
  });
  const updateProject = useUpdateProject();

  function useOptimistic<TVars>(
    run: (vars: TVars) => Promise<unknown>,
    apply: (dto: TrackrsAttentionDto, vars: TVars) => TrackrsAttentionDto,
  ) {
    return useMutation<unknown, Error, TVars, Snapshot>({
      mutationFn: run,
      onMutate: async (vars) => {
        await queryClient.cancelQueries({ queryKey: key });
        const previous = queryClient.getQueryData<TrackrsAttentionDto>(key);
        if (previous) queryClient.setQueryData(key, apply(previous, vars));
        return { previous };
      },
      onError: (_error, _vars, context) => {
        if (context?.previous) queryClient.setQueryData(key, context.previous);
      },
      // Not awaited: the caller's own onSuccess/onError (the toast) must not wait
      // for the refetch — and must still fire if that refetch hangs.
      onSettled: () => {
        void queryClient.invalidateQueries({ queryKey: key });
      },
    });
  }

  const dismiss = useOptimistic<{ kind: DismissableAttentionKind; subjectId: string }>(
    async ({ kind, subjectId }) => unwrap(await api.integrations.dismissTrackrsAttention({ kind, subjectId })),
    (dto, { kind, subjectId }) => removeAttentionRow(dto, kind, subjectId),
  );

  const snooze = useOptimistic<void>(
    async () =>
      unwrap(await api.integrations.dismissTrackrsAttention({ kind: 'whole_page', snoozeDays: SNOOZE_DAYS })),
    (dto) => snoozeAttention(dto, snoozeUntil(new Date())),
  );

  const confirmMatch = useOptimistic<string>(
    async (id) => unwrap(await api.integrations.confirmTrackrsMatch(id)),
    (dto, id) => removeAttentionRow(dto, 'same_project', id),
  );

  const rejectMatch = useOptimistic<string>(
    async (id) => unwrap(await api.integrations.rejectTrackrsMatch(id)),
    (dto, id) => removeAttentionRow(dto, 'same_project', id),
  );

  const deferMatch = useOptimistic<string>(
    async (id) => unwrap(await api.integrations.deferTrackrsMatch(id)),
    (dto, id) => deferAttentionRow(dto, id),
  );

  const createRemaining = useOptimistic<void>(
    async () => unwrap(await api.integrations.createRemainingTrackrsMatches()),
    (dto) => clearSameProjectRows(dto),
  );

  const unlink = useOptimistic<string>(
    async (projectId) => unwrap(await api.integrations.unlinkTrackrsProject(projectId)),
    (dto, projectId) => removeAttentionRow(dto, 'auto_linked', projectId),
  );

  const undoConflict = useOptimistic<string>(
    async (projectId) => unwrap(await api.integrations.undoTrackrsConflict(projectId)),
    (dto, projectId) => removeAttentionRow(dto, 'conflict', projectId),
  );

  // "Arkivér også her": DELETE /api/projects/{id} is the archive (DeleteProjectHandler
  // collapses onto Project.Archive). The attention query lists non-archived projects
  // only, so the row is gone on refetch without a dismissal.
  const archiveProject = useOptimistic<string>(
    async (projectId) => unwrap(await api.projects.remove(projectId)),
    (dto, projectId) => removeAttentionRow(dto, 'remote_deleted', projectId),
  );

  /**
   * Completing the field a "Sager uden …" row is about: customer, case number
   * or start date. The row's kind decides which section the optimistic removal
   * hits; the write is the ordinary project update.
   */
  const completeField = useOptimistic<{
    kind: Extract<AttentionRowKind, 'missing_customer' | 'missing_case_number' | 'missing_start_date'>;
    projectId: string;
    data: Partial<CreateProjectModel>;
  }>(
    async ({ projectId, data }) => updateProject.mutateAsync({ projectId, data }),
    (dto, { kind, projectId }) => removeAttentionRow(dto, kind, projectId),
  );

  return {
    dismiss,
    snooze,
    confirmMatch,
    rejectMatch,
    deferMatch,
    createRemaining,
    unlink,
    undoConflict,
    archiveProject,
    completeField,
  };
}

export type TrackrsAttentionActions = ReturnType<typeof useTrackrsAttentionActions>;
